// TODO: Still two concerns in one file: the user profile (userInfo, messages) and tenants.
// phoneMenus moved out into its own store and logout into composables/useLogout.ts.

import { get } from 'lodash'
import * as Sentry from '@sentry/browser'
import { Service } from '@/services/helpers/ServiceHelper'
import { MS_PER } from '@/services/types/Time'
import type { AvailableLanguage, Maybe } from '@/types/generated'
import type { LoggedUser, UserMessage } from '@/services/types/User'
import type { Tenant } from '@/services/types/Tenant'
import type { LanguageOption } from '@/services/types/Thread'

export const useUserStore = defineStore('user', () => {
  const router = useRouter()
  const { getEffectiveAuthToken } = useEffectiveAuthToken()
  const dateTimeStore = useDateTimeStore()
  const dialog = useDialog()
  const snackbar = useSnackbar()
  const chromeExtensionStore = useChromeExtensionStore()
  const userMenusStore = useUserMenusStore()

  // STATE
  const tenants = ref<Array<Tenant>>([])
  const currentTenantId = useLocalStorage<any>('current-tenant-id', null, {
    listenToStorageChanges: false,
    flush: 'sync'
  })
  const userInfo = ref<LoggedUser | null>(null)
  const fetchingUserInfo = ref(false)
  const fetchingUserServices = ref(false)
  const messages = ref<Array<UserMessage>>([])
  const loadingAvailableLanguages = ref(false)
  const availableLanguages = ref<Array<LanguageOption>>([])
  const locale = ref('en')
  const token = useLocalStorage<string | null>('auth-token', null, { flush: 'sync' })
  let cachedUserServices: Promise<any> | null = null
  let userInfoInFlight: Promise<any> | null = null
  const userServiceRefresh = useIntervalFn(
    () => fetchUserServices(true),
    10 * MS_PER.Minute,
    { immediate: false }
  )

  // GETTERS
  const currentTenantInfo = computed(() => tenants.value.find((tenant) => {
    return tenant.id?.toString() === currentTenantId.value?.toString()
  }))

  const isAdminOrOwner = computed(() => {
    const tenantInformation = userInfo.value?.memberships.find((membership) => {
      return membership.tenant_id === currentTenantId.value
    })
    return ['admin', 'owner'].includes(tenantInformation?.type ?? '')
  })

  // ACTIONS
  const phoneById = (id:string) => tenants.value
    ?.flatMap(tenant => tenant.phone_numbers)
    .find(phone => phone.id?.toString() === id?.toString())

  const phoneByNumber = (number:any) => tenants.value
    ?.flatMap(tenant => tenant.phone_numbers)
    .find(phone => phone.phone_number?.toString() === number?.toString())

  const tenantOwnerOfPhone = (phone:any) => tenants.value.find((tenant) => {
    return tenant.phone_numbers.map(({ phone_number }:any) => phone_number).includes(phone)
  })

  const selectTenant = (tenantId: string | null) => {
    const currentTenant = tenants.value.find((tenant) => {
      return tenant.id?.toString() === tenantId?.toString()
    })
    if (!currentTenant) {
      throw new Error(`Cannot select tenant ${tenantId}: it is not among the user's tenants.`)
    }
    const phoneNumberWithTenantId = (phone:any, tenant:Tenant) => {
      return { ...phone, tenantId: tenant.id }
    }
    const tenantPhoneMenus = currentTenant.phone_numbers
      .map((phone:any) => phoneNumberWithTenantId(phone, currentTenant))
    tenantPhoneMenus.sort((phoneMenuA:any, phoneMenuB:any) => {
      return phoneMenuA.is_seat - phoneMenuB.is_seat
    })
    currentTenantId.value = tenantId
    userMenusStore.setPhoneMenus(tenantPhoneMenus)
  }

  const fetchUserServices = async (skipLoading: boolean) => {
    let authenticated = true
    try {
      userServiceRefresh.pause()
      fetchingUserServices.value = !skipLoading
      const services = await Service.api.userService?.getUserServices()
      tenants.value = services?.tenants || []
      if (services?.tenants) {
        Sentry.setContext('tenants', { tenants: JSON.stringify(services.tenants) })
      }
      let tenantId = currentTenantId.value || services?.tenants[0]?.id
      const currentTenantWholeInfo = tenants.value.find((tenant) => {
        return tenant.id?.toString() === tenantId?.toString()
      })
      if (!currentTenantWholeInfo) {
        tenantId = services?.tenants[0]?.id
      }
      if (tenantId) {
        selectTenant(tenantId)
      }
      await dateTimeStore.initTimezone()
      return userMenusStore.phoneMenus
    } catch (error:any) {
      cachedUserServices = null
      authenticated = error.response?.status !== 401
      if (authenticated) {
        dialog.warning({
          text: 'There was an unexpected problem while loading the user services. Please click the button reload to load them again',
          title: 'Oops',
          action: () => { location.reload() },
          dialogProps: {
            confirmText: 'Reload',
            canClose: false,
            showCancel: false
          }
        })
      }
      if (error.message !== 'Network Error') {
        Sentry.captureException(error)
      }
      return false
    } finally {
      fetchingUserServices.value = false
      // Without a session there is nothing to refresh, and each retry 401s into a logout.
      if (authenticated) {
        userServiceRefresh.resume()
      }
    }
  }

  const initialUserServicesLoad = async () => {
    if (userMenusStore.phoneMenusLoaded) {
      fetchUserServices(false)
      return userMenusStore.phoneMenus
    }
    await fetchUserServices(false)
    return userMenusStore.phoneMenus
  }

  const ensureUserServices = () => {
    if (!cachedUserServices) {
      cachedUserServices = initialUserServicesLoad()
    }
    return cachedUserServices
  }

  const fetchUserInfo = async () => {
    try {
      fetchingUserInfo.value = true
      const authToken = await getEffectiveAuthToken()
      if (!authToken) { return false }
      const userService = Service.api.userService
      if (!userService) {
        snackbar.error('Could not load the user information.')
        return
      }
      const response = await userService.getUserInfo()
      userInfo.value = response.user
      if (response.user) {
        const { id, email, name } = response.user
        Sentry.setContext('user', { id, email, name })
        Sentry.setContext('feature_flags', { feature_flags: currentTenantInfo.value?.feature_flags })
        const mixpanel = useMixpanel()
        mixpanel.identify(id.toString(), email, name)
      }
      if (tenants.value.length) {
        selectTenant(currentTenantId.value)
      }
      messages.value = response.messages.length > 0 ? response.messages : []
      return response.user
    } catch {
      snackbar.error('Could not get the user information.')
      return false
    } finally {
      fetchingUserInfo.value = false
    }
  }

  const loadUserInfo = () => {
    if (userInfoInFlight) {
      return userInfoInFlight
    }
    const promise = fetchUserInfo()
    userInfoInFlight = promise
    promise.finally(() => { userInfoInFlight = null })
    return promise
  }

  const getAvailableLanguages = async () => {
    try {
      if (availableLanguages.value.length > 0) { return }
      loadingAvailableLanguages.value = true
      const languages: Maybe<AvailableLanguage>[] | null = await Service.gql.threadGql?.getAvailableLanguages() || []
      availableLanguages.value = languages?.map((language : Maybe<AvailableLanguage>) => ({
        title: language?.name,
        value: language?.code,
        isPopular: language?.isPopular
      })) || []
    } catch (error) {
      snackbar.error('There was an error loading Available Languages.')
      Sentry.captureException(error)
    } finally {
      loadingAvailableLanguages.value = false
    }
  }

  const refreshUserCache = () => {
    location.reload()
  }

  const authenticate = async ({ email, password } : any) => {
    const response = await Service.api.userService?.authenticate(email, password)
    token.value = response?.token || null
    return response
  }

  const $reset = () => {
    userServiceRefresh.pause()
    userInfo.value = null
    fetchingUserInfo.value = false
    fetchingUserServices.value = false
    tenants.value = []
    availableLanguages.value = []
    locale.value = 'en'
    messages.value = []
    token.value = null
    currentTenantId.value = null
    cachedUserServices = null
    userInfoInFlight = null
    userMenusStore.$reset()
  }

  const refreshLegacyToken = async () => {
    try {
      if (!token.value) { return false }
      const response = await Service.api.userService?.refreshToken(token.value)
      if (!response?.token) {
        throw new Error('No token returned from refresh')
      }
      token.value = response.token
      return true
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('There was an error refreshing the authentication token. Please login again.')
      router.push('/logout')
      return false
    }
  }

  const goToInboxOrHome = async () => {
    try {
      const phoneMenus = await ensureUserServices()
      if (!phoneMenus.length) {
        router.push('/')
        return
      }
      const hasChromeExtension = chromeExtensionStore.extension?.eid
      const firstMenuOfFirstPhone = get(phoneMenus, '0.routes.0.to')
      const canGoToInbox = phoneMenus.length === 1 && firstMenuOfFirstPhone && !hasChromeExtension
      if (canGoToInbox) {
        router.push(firstMenuOfFirstPhone)
        return
      }
      if (hasChromeExtension) {
        router.push('/chrome-extension')
        return
      }
      router.push('/')
    } catch (error) {
      snackbar.error('There was an error navigating to the inbox or home.')
      Sentry.captureException(error)
    }
  }
  return {
    // STATE
    tenants,
    currentTenantId,
    userInfo,
    fetchingUserInfo,
    fetchingUserServices,
    messages,
    loadingAvailableLanguages,
    availableLanguages,
    locale,
    token,
    // GETTERS
    currentTenantInfo,
    isAdminOrOwner,
    // ACTIONS
    phoneById,
    phoneByNumber,
    tenantOwnerOfPhone,
    selectTenant,
    ensureUserServices,
    loadUserInfo,
    getAvailableLanguages,
    refreshUserCache,
    authenticate,
    $reset,
    refreshLegacyToken,
    goToInboxOrHome
  }
})
